import jwt_decode from "jwt-decode";
import { useMsal } from '@azure/msal-react';
import { Box } from '@mui/material';
import { CircularProgress } from '@mui/material';
import { useState } from 'react';
import { useEffect } from 'react';
import { useRef } from 'react';
import { createContext } from 'react';
import { postAdminLogin } from '../../data/hikoFetch';
import { readMahiTokenString, isExpiredToken, storeMahiTokenString } from './mahiToken';
import { hikoAlert } from "../../utils/HikoAlert";
import { InteractionStatus } from "@azure/msal-browser";

//
// Improvements made to MahiContext (that could also be done in HikoContext):
//
// 1. Don't render context children for InteractionStatus.Startup or if mahiTokenString is undefined.
// 2. In hikoFetch, delete the mahiToken from localStorage after 401 Unauthorized.
// 3. Put userRoles into MahiContext and retire useRolesFromMahiContext() hook.
// 4. Upgrade from AuthenticatedRoute to ProtectedRoute (needed by upgrade to react-router-dom)
// 5. In component useEffect calls with async API gets, don't depend on mahiContext. This causes too many refreshes.
//    And anyway either the mahi token in localStorage is valid (in which case you don't want to re-fetch data if token changes)
//               or mahi token in localStorage is invalid (meaning you want to F5 refresh the whole page to get a new token).
// 6. Use didCancel in component useEffect calls with async API gets. Avoids possible memory leaks.
//

const defaultContextInstance = {
    msal: null,
    userRoles: [],
    mahiTokenString: undefined,
    acquireMahiToken: async () => {
        console.error(`This is the default acquireMahiToken function. It doesn't do anything.`)
    }
}

export const MahiContext = createContext(defaultContextInstance)

export function mahiRolesFromMahiContext(mahiContext) {
    let rolesFromMahiToken = [];
    const mahiTokenObject = mahiTokenFromMahiContext(mahiContext)
    if (mahiTokenObject) {
        rolesFromMahiToken = mahiTokenObject.roles
    }
    return rolesFromMahiToken
}

export function mahiNameFromMahiContext(mahiContext) {
    let nameFromMahiToken = ''
    const mahiTokenObject = mahiTokenFromMahiContext(mahiContext)
    if (mahiTokenObject) {
        nameFromMahiToken = mahiTokenObject.name
    }
    return nameFromMahiToken
}

/**
 * 
 * @param {object} mahiContext 
 * @returns E.g. {
 *   "sub":"5f2a765e-4aa9-4c96-b010-025b9b7996e7",
 *   "iss":"mahi-bff-test",
 *   "aud":"mahi-test",
 *   "name":"Mick Websmart",
 *   "email":"mick@websmartdesign.nz",
 *   "roles":["Mahi.UserAdmin","Mahi.NewConnections","Mahi.User","Mahi.IsolationTags"],
 *   "account_id":"0017F00000tmP1MQAU",
 *   "account_name":"EASL",
 *   "account_email":"dw@easl.co.nz",
 *   "iat":1669057067,
 *   "exp":1669661867
 * }
 */
export function mahiTokenFromMahiContext(mahiContext) {
    let mahiTokenObject = {}
    if (mahiContext?.mahiTokenString) {
        try {
            mahiTokenObject = jwt_decode(mahiContext.mahiTokenString)
        } catch (e) {
            hikoAlert('Could not decode mahi token', mahiContext?.mahiTokenString ? 'check local storage' : 'no token found')
        }
    }
    return mahiTokenObject
}


export function MahiContextProvider({children}) {
    const [mahiTokenAndRoles, setMahiTokenAndRoles] = useState({
        mahiTokenString: undefined, // undefined - we've never checked, null = localStorage token is empty, <string> - the mahi token from localStorage
        userRoles: []               // Always a list. Always updated from mahiTokenString when it changes.
    })


 
    const [mahiTokenRequestSettled, setMahiTokenRequestSettled] = useState(false)
    const mahiTokenRequest = useRef(null)

    const msal = useMsal()

    // const counter = useRef(0);
    // console.log('MahiContextProvider started')

    /**
     * 
     * @returns {string|null} mahi token string or null
     */
    const acquireMahiToken = async () => {

        // console.log('MahiContextProvider acquireMahiToken started')

        let theMahiTokenString = readMahiTokenString() // token string or null from localStorage

        // console.log('MahiContextProvider acquireMahiToken checking token', theMahiTokenString)
        if (!theMahiTokenString || isExpiredToken(theMahiTokenString)) {
            //
            // There is no token or the token is expired.
            //
            // We need a new mahi token.
            // Make a call to postAdminLogin() which uses fetchJSONWithAuth() to acquire a new Mahi Token.
            // Only works because postAdminLogin() below uses an Azure Id token, not a Mahi Token.
            // The postAdminLogin call uses 'bffAzure' so will call acquireTokenSilent to refresh Azure AD Token if needed.
            //
            // console.log('MahiContextProvider acquireMahiToken empty or expired token so calling postAdminLogin to log in to user management service', theMahiTokenString)
            theMahiTokenString = await postAdminLogin({ msal }) // Uses the Azure id token, not a Mahi token.

            //
            // Response is a mahi token string or null. Store it in localStorage so we can pick it up if the app is refreshed.
            // Also add it to the MahiContext so context consumers can use it.
            //
            // console.log('MahiContextProvider acquireMahiToken about to stringify and store theMahiTokenString', theMahiTokenString)
            storeMahiTokenString(theMahiTokenString) // local storage
            
            //
            // Decode the jwt token so we can get the user roles.
            //
            const mahiTokenObject = mahiTokenFromMahiContext({mahiTokenString: theMahiTokenString})
            
            //
            // Update state. This will (should) cause a refresh of all context consumer components.
            //
            setMahiTokenAndRoles({
                mahiTokenString: theMahiTokenString,
                userRoles: mahiTokenObject?.roles || [],
            })
        }
        // console.log('MahiContextProvider acquireMahiToken returns theMahiTokenString', theMahiTokenString)
        return theMahiTokenString
    }

    useEffect(() => {
        if (msal.inProgress !== InteractionStatus.None || msal.accounts.length === 0) {
            return
        }
        const existingToken = readMahiTokenString()
        if (existingToken && !isExpiredToken(existingToken)) {
            return
        }
        if (mahiTokenRequest.current) {
            return
        }
        // Login success is handled before the sign-in button can listen for it, so create the Mahi token here.
        mahiTokenRequest.current = acquireMahiToken().finally(() => {
            setMahiTokenRequestSettled(true)
        })
    // eslint-disable-next-line
    }, [msal.inProgress, msal.accounts])

    useEffect(() => {
        // console.log('MahiContextProvider useEffect getting mahiToken')
        let theMahiTokenString = readMahiTokenString() // token string or null from localStorage

        if (theMahiTokenString !== mahiTokenAndRoles.mahiTokenString) {
            //
            // Mahi Token has changed. Refresh the context.
            //

            //
            // Decode the jwt token so we can get the user roles.
            //
            // console.log('MahiContextProvider useEffect about to call mahiTokenFromMahiContext')
            const mahiTokenObject = mahiTokenFromMahiContext({mahiTokenString: theMahiTokenString})
            
            //
            // Will refresh all components that consume this context.
            //
            setMahiTokenAndRoles({
                mahiTokenString: theMahiTokenString,
                userRoles: mahiTokenObject?.roles || [],
            })

        }
        // console.log('MahiContextProvider useEffect returns theMahiTokenString', theMahiTokenString)

    }, [mahiTokenAndRoles])


    // console.log('MahiContextProvider COUNT, !!mahiTokenString, msal', counter.current++, !!mahiTokenAndRoles.mahiTokenString, msal)
    
    const signedIn = msal.accounts.length > 0
    const hasMahiToken = Boolean(mahiTokenAndRoles.mahiTokenString)
    const waitingForMahiToken = signedIn && !hasMahiToken && !mahiTokenRequestSettled

    if (msal.inProgress === InteractionStatus.Startup) {
        // console.log('MahiContextProvider hiding children because msal is starting up')
        return null
    }

    return (
      <MahiContext.Provider value={{
        mahiTokenString: mahiTokenAndRoles.mahiTokenString,
        userRoles: mahiTokenAndRoles.userRoles,
        acquireMahiToken,
        msal,
      }}>
        {
            mahiTokenAndRoles.mahiTokenString !== undefined && !waitingForMahiToken
            ?
            children
            :
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
                <CircularProgress color="secondary" />
            </Box>
        }
      </MahiContext.Provider>
    )
}